package kz.dentalcrm.app.core.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.kizitonwose.calendar.compose.CalendarState
import com.kizitonwose.calendar.compose.VerticalCalendar
import com.kizitonwose.calendar.compose.rememberCalendarState
import com.kizitonwose.calendar.core.CalendarDay
import com.kizitonwose.calendar.core.CalendarMonth
import com.kizitonwose.calendar.core.DayPosition
import com.kizitonwose.calendar.core.OutDateStyle
import com.kizitonwose.calendar.core.daysOfWeek
import com.kizitonwose.calendar.core.minusMonths
import com.kizitonwose.calendar.core.now
import com.kizitonwose.calendar.core.plusMonths
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronLeft
import compose.icons.feathericons.ChevronRight
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.done
import kotlin.time.ExperimentalTime
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.launch
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.Month
import kotlinx.datetime.YearMonth
import kotlinx.datetime.yearMonth
import kz.dentalcrm.app.screens.appointments.add.formatDateReadable
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalTime::class)
@Composable
actual fun AppointmentDateTimeDialog(
    startDateTime: LocalDateTime?,
    onDismissRequest: () -> Unit,
    onDateSelected: (LocalDateTime) -> Unit,
) {
    val today = remember { LocalDate.now() }
    val currentMonth = remember(today) { today.yearMonth }
    val startMonth = remember { currentMonth.minusMonths(24) }
    val endMonth = remember { currentMonth.plusMonths(24) }
    val firstDaysOfWeek = remember { daysOfWeek() }

    var selectedDate by remember { mutableStateOf(startDateTime?.date ?: today) }
    var hourString by remember {
        mutableStateOf(startDateTime?.hour?.toString()?.padStart(2, '0') ?: "09")
    }
    var minuteString by remember {
        mutableStateOf(startDateTime?.minute?.toString()?.padStart(2, '0') ?: "00")
    }

    val hourInt = hourString.toIntOrNull()
    val minuteInt = minuteString.toIntOrNull()
    val isValid = hourInt != null && hourInt in 0..23 &&
            minuteInt != null && minuteInt in 0..59 &&
            hourString.isNotBlank() && minuteString.isNotBlank()

    val confirmedDateTime = if (isValid && hourInt != null && minuteInt != null) {
        LocalDateTime(
            date = selectedDate,
            time = LocalTime(hour = hourInt, minute = minuteInt),
        )
    } else null

    val calendarState = rememberCalendarState(
        startMonth = startMonth,
        endMonth = endMonth,
        firstVisibleMonth = selectedDate.yearMonth,
        firstDayOfWeek = firstDaysOfWeek.first(),
        outDateStyle = OutDateStyle.EndOfGrid,
    )
    val visibleMonth = rememberFirstVisibleMonthAfterScroll(calendarState)
    val coroutineScope = rememberCoroutineScope()

    Dialog(onDismissRequest = onDismissRequest) {
        Column(
            modifier = Modifier
                .widthIn(max = 400.dp)
                .wrapContentHeight()
                .background(
                    color = DefaultTheme.colors.surface,
                    shape = RoundedCornerShape(24.dp),
                )
                .padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Month navigation title
            MonthTitle(
                currentMonth = visibleMonth.yearMonth,
                onPrevious = {
                    coroutineScope.launch {
                        calendarState.animateScrollToMonth(
                            calendarState.firstVisibleMonth.yearMonth.minusMonths(1)
                        )
                    }
                },
                onNext = {
                    coroutineScope.launch {
                        calendarState.animateScrollToMonth(
                            calendarState.firstVisibleMonth.yearMonth.plusMonths(1)
                        )
                    }
                },
            )

            Spacer(modifier = Modifier.height(4.dp))

            // Days of week header (Пн Вт Ср Чт Пт Сб Вс)
            Row(modifier = Modifier.fillMaxWidth()) {
                for (dayOfWeek in firstDaysOfWeek) {
                    Text(
                        modifier = Modifier.weight(1f),
                        textAlign = TextAlign.Center,
                        text = dayOfWeek.ruShort(),
                        style = DefaultTheme.typography.labelRegular,
                        color = DefaultTheme.colors.onSurfaceVariant,
                    )
                }
            }

            Spacer(modifier = Modifier.height(4.dp))

            // Month calendar — vertical, paged, fixed height (6 rows × ~44dp)
            VerticalCalendar(
                modifier = Modifier.fillMaxWidth().height(264.dp),
                state = calendarState,
                calendarScrollPaged = true,
                dayContent = { day ->
                    MonthDay(
                        day = day,
                        today = today,
                        selected = day.date == selectedDate,
                        onClick = { clicked ->
                            if (clicked.position == DayPosition.MonthDate) {
                                selectedDate = clicked.date
                            }
                        },
                    )
                },
            )

            Spacer(modifier = Modifier.height(12.dp))
            HorizontalDivider(color = DefaultTheme.colors.dividerPrimary)
            Spacer(modifier = Modifier.height(12.dp))

            // Time input HH:mm
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
            ) {
                TimeBox(
                    value = hourString,
                    label = "час",
                    onValueChange = { raw ->
                        val digits = raw.filter { it.isDigit() }
                        if (digits.length <= 2) {
                            val h = digits.toIntOrNull()
                            if (digits.isEmpty() || (h != null && h <= 23)) hourString = digits
                        }
                    },
                )
                Text(
                    text = ":",
                    style = TextStyle(fontSize = 38.sp, color = DefaultTheme.colors.onSurface),
                    modifier = Modifier.padding(horizontal = 8.dp),
                )
                TimeBox(
                    value = minuteString,
                    label = "минут",
                    onValueChange = { raw ->
                        val digits = raw.filter { it.isDigit() }
                        if (digits.length <= 2) {
                            val m = digits.toIntOrNull()
                            if (digits.isEmpty() || (m != null && m <= 59)) minuteString = digits
                        }
                    },
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Confirm button
            UIButton(
                modifier = Modifier.fillMaxWidth(),
                enabled = isValid,
                text = if (confirmedDateTime != null) {
                    stringResource(Res.string.done) + ": " + confirmedDateTime.formatDateReadable()
                } else {
                    stringResource(Res.string.done)
                },
                onClick = {
                    confirmedDateTime?.let {
                        onDateSelected(it)
                        onDismissRequest()
                    }
                },
            )
        }
    }
}

// ─────────────────────────────────────────────
// Month title row with prev/next navigation
// ─────────────────────────────────────────────

@Composable
private fun MonthTitle(
    currentMonth: YearMonth,
    onPrevious: () -> Unit,
    onNext: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().height(40.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .size(36.dp)
                .clip(CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onPrevious,
                ),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                imageVector = FeatherIcons.ChevronLeft,
                contentDescription = "Previous month",
                tint = DefaultTheme.colors.onSurface,
                modifier = Modifier.size(20.dp),
            )
        }
        Text(
            modifier = Modifier.weight(1f),
            text = "${currentMonth.month.ruName()} ${currentMonth.year}",
            style = DefaultTheme.typography.bodyMediumMedium,
            textAlign = TextAlign.Center,
            color = DefaultTheme.colors.onSurface,
        )
        Box(
            modifier = Modifier
                .size(36.dp)
                .clip(CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onNext,
                ),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                imageVector = FeatherIcons.ChevronRight,
                contentDescription = "Next month",
                tint = DefaultTheme.colors.onSurface,
                modifier = Modifier.size(20.dp),
            )
        }
    }
}

// ─────────────────────────────────────────────
// Day cell
// ─────────────────────────────────────────────

@Composable
private fun MonthDay(
    day: CalendarDay,
    today: LocalDate,
    selected: Boolean,
    onClick: (CalendarDay) -> Unit,
) {
    val isMonthDate = day.position == DayPosition.MonthDate
    val isToday = isMonthDate && day.date == today

    Box(
        modifier = Modifier
            .aspectRatio(1f)
            .padding(3.dp)
            .clip(CircleShape)
            .background(
                color = when {
                    selected && isMonthDate -> DefaultTheme.colors.primary
                    isToday -> DefaultTheme.colors.primarySubdued
                    else -> Color.Transparent
                }
            )
            .clickable(
                enabled = isMonthDate,
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = { onClick(day) },
            ),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = day.date.day.toString(),
            style = DefaultTheme.typography.bodyMediumMedium,
            color = when {
                selected && isMonthDate -> DefaultTheme.colors.onPrimary
                isMonthDate -> DefaultTheme.colors.onSurface
                else -> DefaultTheme.colors.onSurface.copy(alpha = 0.25f)
            },
            fontSize = 14.sp,
        )
    }
}

// ─────────────────────────────────────────────
// HH / MM text input field
// ─────────────────────────────────────────────

@Composable
private fun TimeBox(
    value: String,
    label: String,
    onValueChange: (String) -> Unit,
) {
    var isFocused by remember { mutableStateOf(false) }
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Box(
            modifier = Modifier
                .width(88.dp)
                .height(72.dp)
                .background(
                    color = if (isFocused) Color.Transparent
                    else DefaultTheme.colors.surfaceAlternative,
                    shape = RoundedCornerShape(8.dp),
                )
                .border(
                    width = if (isFocused) 2.dp else 1.dp,
                    color = if (isFocused) DefaultTheme.colors.primary
                    else DefaultTheme.colors.outline,
                    shape = RoundedCornerShape(8.dp),
                ),
            contentAlignment = Alignment.Center,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                textStyle = TextStyle(
                    fontSize = 36.sp,
                    textAlign = TextAlign.Center,
                    color = DefaultTheme.colors.onSurface,
                ),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                cursorBrush = SolidColor(DefaultTheme.colors.primary),
                modifier = Modifier
                    .fillMaxWidth()
                    .wrapContentHeight(Alignment.CenterVertically)
                    .padding(horizontal = 8.dp),
            )
        }
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = DefaultTheme.colors.onSurfaceVariant,
        )
    }
}

// ─────────────────────────────────────────────
// Localization helpers — Russian
// ─────────────────────────────────────────────

private fun DayOfWeek.ruShort(): String = when (this) {
    DayOfWeek.MONDAY -> "Пн"
    DayOfWeek.TUESDAY -> "Вт"
    DayOfWeek.WEDNESDAY -> "Ср"
    DayOfWeek.THURSDAY -> "Чт"
    DayOfWeek.FRIDAY -> "Пт"
    DayOfWeek.SATURDAY -> "Сб"
    DayOfWeek.SUNDAY -> "Вс"
}

private fun Month.ruName(): String = when (this) {
    Month.JANUARY -> "Январь"
    Month.FEBRUARY -> "Февраль"
    Month.MARCH -> "Март"
    Month.APRIL -> "Апрель"
    Month.MAY -> "Май"
    Month.JUNE -> "Июнь"
    Month.JULY -> "Июль"
    Month.AUGUST -> "Август"
    Month.SEPTEMBER -> "Сентябрь"
    Month.OCTOBER -> "Октябрь"
    Month.NOVEMBER -> "Ноябрь"
    Month.DECEMBER -> "Декабрь"
}

// ─────────────────────────────────────────────
// Track visible month while scrolling
// ─────────────────────────────────────────────

@Composable
private fun rememberFirstVisibleMonthAfterScroll(state: CalendarState): CalendarMonth {
    val visibleMonth = remember(state) { mutableStateOf(state.firstVisibleMonth) }
    LaunchedEffect(state) {
        snapshotFlow { state.isScrollInProgress }
            .filter { !it }
            .collect { visibleMonth.value = state.firstVisibleMonth }
    }
    return visibleMonth.value
}
